"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.StoreButton = void 0;
/**
 * Copyright (c) 2024 BrightSpike
 * All rights reserved.
 *
 * This source code is unlicensed.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 *
 * For detailed license information, please contact Brightspike at (info@brightspike.io).
 * @author Austin Weidler
 */
const Block_1 = require("./Block");
const pixi_js_1 = require("pixi.js");
const WigglyText_1 = require("../objects/WigglyText");
const i18next_1 = __importDefault(require("i18next"));
const checkmark_svg_1 = __importDefault(require("../../assets/svg/checkmark.svg"));
const lock_svg_1 = __importDefault(require("../../assets/svg/lock.svg"));
const Global_1 = require("../Global");
const gsap_1 = require("gsap");
const colors_1 = require("../config/colors");
const settings_1 = require("../config/settings");
const ColorItem_1 = require("./ColorItem");
const AssetManager_1 = __importDefault(require("../managers/AssetManager"));
class StoreButton extends Block_1.Block {
    constructor(app, gameManager, group, item) {
        super(app);
        this.gameManager = gameManager;
        this.group = group;
        this.item = item;
        this.sizer = new pixi_js_1.Graphics();
        this.addChild(this.sizer);
        this.background = this.makeItem();
        this.check = pixi_js_1.Sprite.from(checkmark_svg_1.default);
        this.check.visible = false;
        this.background.addChild(this.check);
        this.lock = pixi_js_1.Sprite.from(lock_svg_1.default);
        this.background.addChild(this.lock);
        this.addChild(this.background);
        this.cost = new pixi_js_1.Container();
        this.cost.addChild(new pixi_js_1.Graphics());
        const t = (0, Global_1.replaceZeroWithO)(this.item.cost_brushes ? this.item.cost_brushes : this.item.cost_streak);
        this.cost.addChild(new pixi_js_1.Text(t, Block_1.Block.textStyle(24, false)));
        this.cost.addChild(pixi_js_1.Sprite.from(this.item.cost_brushes ? AssetManager_1.default.brushpoint : AssetManager_1.default.fire));
        this.addChild(this.cost);
        this.splatter = new pixi_js_1.Graphics();
        this.addChild(this.splatter);
        this.tm = gsap_1.gsap.timeline({ paused: true, autoRemoveChildren: false }).to(this.splatter, {
            duration: 0.2,
            onUpdate: () => {
                var _a;
                const p = this.tm.progress();
                (_a = this.splatter) === null || _a === void 0 ? void 0 : _a.clear().beginFill(settings_1.Settings.whiteColor).drawCircle(this.plate.width / 2, this.plate.height / 2, this.plate.width / 2 * p).endFill();
            },
            ease: 'none'
        });
        const txt = group.name + '.' + item.value;
        this.text = this.addChild(new WigglyText_1.WigglyText(this.app, i18next_1.default.t(txt).toString(), Block_1.Block.textStyle(28)));
        this.text.anchor.set(0.5, 0);
        this.subs.add(this.gameManager.playerManager.playerData$.subscribe(() => {
            this.update();
        }));
        this.update();
        this.background.mask = this.splatter;
    }
    select() {
        (0, Global_1.fade)(this.check, 1, 0.1).then();
        return this;
    }
    deselect() {
        (0, Global_1.fade)(this.check, 0, 0.1).then();
        return this;
    }
    get name() {
        return this.text.text;
    }
    get owns() {
        return this.gameManager.playerManager.owns(this.group.name, this.item.value);
    }
    get afford() {
        return (this.item.cost_brushes && this.gameManager.playerManager.brushes >= this.item.cost_brushes)
            || (this.item.cost_streak && this.gameManager.playerManager.streak >= this.item.cost_streak);
    }
    get plate() {
        return (this.background.getChildAt(1));
    }
    update() {
        var _a;
        this.check.anchor.set(0.5);
        this.check.scale.set((0, Global_1.px)(0.13));
        this.check.position.set(this.plate.width / 2, this.plate.height / 2);
        this.lock.anchor.set(0.5);
        this.lock.scale.set((0, Global_1.px)(0.17));
        this.lock.alpha = 0.7;
        this.lock.position.set(this.plate.width / 2, this.plate.height / 2);
        this.lock.visible = !this.afford && !this.owns;
        const lbg = this.makeBox({
            backgroundColor: settings_1.Settings.whiteColor,
            border: 2.5,
            radius: 3,
            width: 65,
            height: 30
        }, ((_a = this.cost) === null || _a === void 0 ? void 0 : _a.getChildAt(0)));
        const lbrush = (this.cost.getChildAt(2));
        lbrush.scale.set((0, Global_1.px)(this.item.cost_brushes ? 0.165 : 0.11));
        lbrush.anchor.set(1, 0.5);
        lbrush.position.set(lbg.x + lbg.width - (0, Global_1.px)(this.item.cost_brushes ? 7 : 12), lbg.y + lbg.height / 2);
        const ltext = (this.cost.getChildAt(1));
        ltext.anchor.set(1, 0.5);
        ltext.position.set(lbrush.x - lbrush.width - (0, Global_1.px)(4), lbg.y + lbg.height / 2 + (0, Global_1.px)(2));
        // This is just to force the size of the plate (even when there is a mask applied)
        this.splatter.position = this.background.position;
        this.sizer.position = this.background.position;
        this.sizer.clear().beginFill(settings_1.Settings.whiteColor, 0.0000001)
            .drawRect(0, 0, this.plate.width, this.plate.height).endFill();
        if (!this._texture) {
            this._texture = this.app.renderer.generateTexture(this.background);
        }
        try {
            if (!this.owns) {
                this.cost.visible = true;
                this.tm.seek('-=0', false);
                this.background.enabled = false;
            }
            else {
                this.cost.visible = false;
                this.tm.autoRemoveChildren = true;
                this.tm.play(0);
                this.background.enabled = true;
            }
        }
        catch (e) {
            console.log(e);
        }
    }
    makeItem() {
        return new ColorItem_1.ColorItem(this.app, this.sample);
    }
    get sample() {
        // TODO: Make this sample not dependent on colors, but can be other store item types
        return colors_1.colors[this.item.value];
    }
    get texture() {
        return this._texture;
    }
    destroy(_options) {
        var _a;
        (_a = this.tm) === null || _a === void 0 ? void 0 : _a.kill();
        super.destroy(_options);
        this._texture.destroy(true);
    }
    layout() {
        super.layout();
        this.update();
        this.background.position.set(0, (0, Global_1.px)(30));
        this.cost.position.set(this.background.width / 2 - this.cost.width / 2, this.background.y
            + this.background.height - this.cost.height / 3);
        this.text.position.set(this.background.width / 2, 0);
    }
}
exports.StoreButton = StoreButton;
